import React from "react";

import {
  ChartLineUpIcon,
  CirclesThreeIcon,
  CloudIcon,
  CodeIcon,
  CubeIcon,
  Files2Icon,
  GearIcon,
  getAccountNoun,
  NotificationsIcon,
  PlugsConnectedIcon,
  PresentationChartIcon,
  type NavItem,
} from "@stacklet/ui";

import { useCloudProvider } from "app/contexts/ProviderContext";
import { useHasScopePermission, useStickyState } from "app/hooks";

/**
 * Starts a page's queries when its nav item is hovered.
 *
 * The preload modules are imported here rather than at the top of the file: the
 * sidebar is part of the app shell, and a static import would put those pages'
 * queries in the entry bundle for every page. Each chunk is small and the
 * import is cached, so the queries still go out well before the click.
 *
 * A preload that fails to load costs nothing: the click fetches as if the hover
 * never happened.
 */
function preloadPolicies() {
  import("app/views/admin/policies/policiesPreload")
    .then((module) => module.preloadPoliciesOnHover())
    .catch(() => {});
}

function preloadAccounts() {
  import("app/views/admin/accounts/accountsPreload")
    .then((module) => module.preloadAccountsOnHover())
    .catch(() => {});
}

function preloadBindings() {
  import("app/views/admin/bindings/bindingsPreload")
    .then((module) => module.preloadBindingsOnHover())
    .catch(() => {});
}

function preloadUsersSettings() {
  import("app/views/settings/access-control/usersSettingsPreload")
    .then((module) => module.preloadUsersSettingsOnHover())
    .catch(() => {});
}

const rotatedPlugsIcon =
  (deg: number) =>
  ({ className }: { className?: string }) =>
    React.createElement(PlugsConnectedIcon, {
      className,
      style: { transform: `rotate(${deg}deg)` },
    });

const PlugsConnectedRotatedNegIcon = rotatedPlugsIcon(-45);

import { paths } from "app/constants/paths";

import type { useConfig } from "app/contexts";

interface Dashboard {
  label: string;
  Icon?: NavItem["Icon"];
  id: string;
  key: string;
  target: string;
}

export function useNavItems(
  enabledDashboards: Dashboard[],
  config: ReturnType<typeof useConfig>,
) {
  const hasPermission = useHasScopePermission();
  const isAdmin = hasPermission("system", "EDIT");
  const accountNoun = getAccountNoun(useCloudProvider());

  // Persist whether the Reports section is expanded across reloads.
  const [reportsExpanded, setReportsExpanded] = useStickyState<boolean>(
    true,
    "sidebar:reports-expanded",
  );

  const hiddenDashboardKeys = config?.hidden_dashboard_keys || [];
  const shouldShowCloudDashboard = !hiddenDashboardKeys.includes("cloud");

  // Footer group: notifications + settings, then the API link. The first item
  // carries `startsGroup` so it is separated from the main nav. Admins get the
  // full settings area (landing on the users list, which their Settings item
  // preloads); everyone else gets a Settings entry that opens their own profile
  // — the one settings page that is not gated on system:EDIT — and preloads
  // nothing, so no one fetches a users list they can't see.
  const footerNavItems: NavItem[] = [
    ...(isAdmin
      ? [
          {
            label: "Notifications",
            id: "notifications",
            Icon: NotificationsIcon,
            target: paths.notificationsOverview,
            activePaths: [paths.notifications],
          },
          {
            label: "Settings",
            id: "settings",
            Icon: GearIcon,
            target: paths.users,
            activePaths: [paths.settings],
            onPreload: preloadUsersSettings,
          },
        ]
      : [
          {
            label: "Settings",
            id: "settings",
            Icon: GearIcon,
            target: paths.settingsProfile,
            activePaths: [paths.settings],
          },
        ]),
    {
      label: "Stacklet API",
      Icon: CodeIcon,
      target: paths.api,
      isExternalLink: false,
      id: "gql-api",
    },
  ];
  footerNavItems[0] = { ...footerNavItems[0], startsGroup: true };

  const navItems: NavItem[] = [
    {
      label: "Resources",
      Icon: CubeIcon,
      id: "resources",
      target: paths.resources,
      activePaths: [paths.cloudops],
      startsGroup: true,
    },
    {
      label: accountNoun.pluralTitle,
      Icon: CirclesThreeIcon,
      target: paths.adminAccounts,
      id: "admin-accounts",
      activePaths: [paths.adminAccountGroups],
      onPreload: preloadAccounts,
    },
    {
      label: "Bindings",
      Icon: PlugsConnectedRotatedNegIcon,
      id: "admin-bindings",
      target: paths.adminBindings,
      onPreload: preloadBindings,
    },
    {
      label: "Policies",
      Icon: Files2Icon,
      id: "admin-policies",
      target: paths.adminPolicies,
      activePaths: [paths.adminPolicyCollections, paths.adminRepositories],
      onPreload: preloadPolicies,
    },
    {
      label: "Reports",
      id: "reports",
      Icon: PresentationChartIcon,
      defaultExpanded: reportsExpanded,
      onExpandedChange: setReportsExpanded,
      children: [
        ...(shouldShowCloudDashboard
          ? [
              {
                label: "Clouds",
                Icon: CloudIcon,
                id: "clouds",
                target: paths.dashboardsCloud,
              },
            ]
          : []),
        {
          label: "Stacklet Activity",
          Icon: ChartLineUpIcon,
          target: paths.activity,
          id: "activity",
        },
        ...enabledDashboards,
      ],
    },
    ...footerNavItems,
  ];

  return navItems;
}
